import React, { useEffect, useState } from "react";
import AuthService from "../../../api/services/AuthService";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponent/CustomAlertMessage";
import LoaderHelper from "../../../customComponent/Loading/LoaderHelper";
import DataTableBase from "../../../customComponent/DataTable";
import * as XLSX from 'xlsx';
import PayoutDetailsModal from "./PayoutDetailsModal";
import DocumentViewerModal from "./DocumentViewerModal";

const GenerateAgreements = () => {
    const [affiliates, setAffiliates] = useState([]);
    const [allData, setAllData] = useState([]);
    const [showEditorModal, setShowEditorModal] = useState(false);
    const [documentData, setDocumentData] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedInvestment, setSelectedInvestment] = useState(null);
    const [expandedRows, setExpandedRows] = useState([]);
    
    const searchPackages = (e) => {
        const term = e.target.value.toLowerCase();
        const keysToSearch = ["emailId", "firstName", "lastName",];
        const filtered = allData.filter(obj =>
            keysToSearch.some(key =>
                obj[key]?.toString().toLowerCase().includes(term)
            )
        );
        setAffiliates(filtered);
    };
    useEffect(() => {
        fetchAffiliates();
    }, []);

    const fetchAffiliates = async () => {
        LoaderHelper.loaderStatus(true);
        try {
            const result = await AuthService.getaffiliateInvestmentList();
            if (result.success) {
                setAffiliates(result.data || []);
                setAllData(result.data || []);
            } else {
                alertErrorMessage(result.message);
            }
        } catch {
            alertErrorMessage("Failed to fetch affiliates");
        } finally {
            LoaderHelper.loaderStatus(false);
        }
    };
    const generateAgreement = async (user, investment) => {
        LoaderHelper.loaderStatus(true);
        try {
            const response = await AuthService.generateAgreement(investment._id);
            if (response.success) {
                alertSuccessMessage("Agreement generated successfully");
                if (response.data) {
                    setDocumentData(response.data);
                    setSelectedUser(user);
                    setSelectedInvestment(investment);
                    setShowEditorModal(true);
                } else {
                    alertErrorMessage("Document data not found in response");
                }
            } else {
                alertErrorMessage(response.message || "Failed to generate agreement");
            }
        } catch (error) {
            console.error("Failed to generate agreement:", error);
            alertErrorMessage("Failed to generate agreement");
        } finally {
            LoaderHelper.loaderStatus(false);
        }
    };

    const handleCloseEditor = () => {
        setShowEditorModal(false);
        setDocumentData(null);
        setSelectedUser(null);
        setSelectedInvestment(null);
    };

    const toggleRow = (userId) => {
        setExpandedRows(prev => 
            prev.includes(userId) 
                ? prev.filter(id => id !== userId)
                : [userId]
        );
    };

    const handleSaveAgreement = (response) => {
        // Refresh the list after saving
        fetchAffiliates();
    };
    const columns = [
        { 
            name: '', 
            width: '60px',
            cell: row => (
                <button 
                    className="btn btn-sm btn-link p-0" 
                    onClick={() => toggleRow(row._id)}
                >
                    <i className={`ri-arrow-${expandedRows.includes(row._id) ? 'down' : 'right'}-s-line`} 
                       style={{ fontSize: '20px', color: '#393838' }}></i>
                </button>
            )
        },
        { name: 'Sr No.', selector: (row, idx) => idx + 1, width: '80px' },
        { name: 'Name', selector: row => `${row.firstName} ${row.lastName}`, width: '200px' },
        { name: 'Email', selector: row => row.emailId, width: '250px' },
        { name: 'Total Investments', selector: row => row.investments?.length || 0, width: '140px' },
        { name: 'Total Amount', selector: row => {
            const total = row.investments?.reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0) || 0;
            return total.toFixed(2);
        }, width: '140px' },
    ];
    const downloadAffiliateList = (affiliateList, format = 'excel') => {
        if (!affiliateList?.length) {
            alert("No data available to download.");
            return;
        }

        const finalRows = [];

        affiliateList.forEach(user => {
            const userName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
            const email = user.emailId || '';

            user.investments?.forEach((inv) => {
                const amount = typeof inv.amount === 'object' ? inv.amount?.$numberDecimal : inv.amount || 0;
                const selfROI = inv.self_roi_percent || 0;
                const roiDist = inv.roi_distribution || [];

                if (roiDist.length === 0) {
                    finalRows.push({
                        Name: userName,
                        Email: email,
                        Investment: amount,
                        SelfROI: selfROI,
                        Level: '',
                        ROIPercent: '',
                        UplineName: '',
                        UplineEmail: ''
                    });
                } else {
                    roiDist.forEach((roi, index) => {
                        const upline = roi.user || {};
                        finalRows.push({
                            Name: index === 0 ? userName : '',
                            Email: index === 0 ? email : '',
                            Investment: index === 0 ? amount : '',
                            SelfROI: index === 0 ? selfROI : '',
                            Level: roi.level,
                            ROIPercent: roi.roi_percent,
                            UplineName: `${upline.firstName || ''} ${upline.lastName || ''}`.trim(),
                            UplineEmail: upline.emailId || ''
                        });
                    });
                }
            });
        });

        if (format === 'excel') {
            const worksheet = XLSX.utils.json_to_sheet(finalRows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Affiliate ROI');
            const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });            
        }
    };

    const customScrollbarStyles = `
    .custom-scrollbar::-webkit-scrollbar {
      width: 8px;
    }
    
    .custom-scrollbar::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 10px;
    }
    
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 10px;
    }
    
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
    
    /* Firefox */
    .custom-scrollbar {
      scrollbar-width: thin;
      scrollbar-color: #888 #f1f1f1;
    }
  `;
    return (
        <div id="layoutSidenav_content">
            <style>{customScrollbarStyles}</style>
            <div className="container-xl px-4 mt-4">
                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h5>Agreement List</h5>
                        <div className="col-5">
                            <input style={{ border: "1px solid #ccc" }}
                                className="form-control form-control-solid"
                                type="text"
                                placeholder="Search here..."
                                onChange={searchPackages}
                            />
                        </div>
                        {/* <div>
                            <button className="btn btn-success btn-sm mx-2" onClick={() => downloadAffiliateList(affiliates, 'excel')}>Export Excel</button>
                            <button className="btn btn-danger btn-sm" onClick={() => downloadAffiliateList(affiliates, 'pdf')}>Export PDF</button>
                        </div> */}
                    </div>
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead style={{ backgroundColor: '#393838', color: '#ffdc89' }}>
                                    <tr>
                                        <th style={{ width: '60px' }}></th>
                                        <th style={{ width: '80px' }}>Sr No.</th>
                                        <th style={{ width: '200px' }}>Name</th>
                                        <th style={{ width: '250px' }}>Email</th>
                                        <th style={{ width: '140px' }}>Total Investments</th>
                                        <th style={{ width: '140px' }}>Total Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {affiliates.map((user, idx) => (
                                        <React.Fragment key={user._id}>
                                            <tr onClick={() => toggleRow(user._id)} style={{cursor: 'pointer', background: expandedRows.includes(user._id)? '#393838' : 'transparent' }}>
                                                <td >
                                                    <button 
                                                        className="btn btn-sm" 
                                                        
                                                        style={{
                                                            border: 'none',
                                                            padding: '0px 0px',
                                                            borderRadius: '5px',
                                                        }}
                                                        title={expandedRows.includes(user._id) ? "Collapse" : "Expand"}
                                                    >
                                                        <i className={`${expandedRows.includes(user._id) ? 'fa fa-arrow-down' : 'fa fa-arrow-right'}`} 
                                                           style={{ fontSize: '18px', fontWeight: 'bold' }}></i>
                                                    </button>
                                                </td>
                                                <td>{idx + 1}</td>
                                                <td>{user.firstName} {user.lastName}</td>
                                                <td>{user.emailId}</td>
                                                <td>{user.investments?.length || 0}</td>
                                                <td>{user.investments?.reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0).toFixed(2)}</td>
                                            </tr>
                                            {expandedRows.includes(user._id) && (
                                                <tr>
                                                    <td colSpan="6" style={{ padding: '0', backgroundColor: '#393838' }}>
                                                        <div style={{ padding: '15px', margin: '10px' }}>
                                                            <h6 style={{ color: 'white', marginBottom: '15px' }}>
                                                                <i className="ri-money-dollar-circle-line me-2"></i>
                                                                Investment Details
                                                            </h6>
                                                            <div className="table-responsive">
                                                                <table className="table table-sm table-bordered">
                                                                    <thead >
                                                                        <tr>
                                                                            <th>Sr No.</th>
                                                                            <th>Amount</th>
                                                                            <th>Currency</th>
                                                                            <th>Self ROI %</th>
                                                                            <th>Total ROI Returned</th>
                                                                            <th>Status</th>
                                                                            <th>Investment Date</th>
                                                                            <th>Action</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {user.investments?.map((investment, invIdx) => (
                                                                            <tr key={investment._id}>
                                                                                <td>{invIdx + 1}</td>
                                                                                <td>{parseFloat(investment.amount || 0).toFixed(2)}</td>
                                                                                <td>{investment.currency || 'N/A'}</td>
                                                                                <td>{investment.self_roi_percent || 0}%</td>
                                                                                <td>{parseFloat(investment.total_self_roi_returned || 0).toFixed(2)}</td>
                                                                                <td>
                                                                                    <span className={`badge bg-${investment.status === 'ACTIVE' ? 'success' : 'secondary'}`}>
                                                                                        {investment.status}
                                                                                    </span>
                                                                                </td>
                                                                                <td>{new Date(investment.investmentDate).toLocaleDateString()}</td>
                                                                                <td>
                                                                                    <button 
                                                                                        disabled={investment.status !== 'ACTIVE'}
                                                                                        className="btn btn-sm btn-primary"
                                                                                        onClick={() => generateAgreement(user, investment)}
                                                                                    >
                                                                                        <i className="ri-file-text-line me-1"></i>
                                                                                        Generate Agreement
                                                                                    </button>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Document Viewer Modal */}
            {showEditorModal && documentData && selectedUser && (
                <DocumentViewerModal
                    show={showEditorModal}
                    onClose={handleCloseEditor}
                    documentData={documentData}
                    user={selectedUser}
                    investment={selectedInvestment}
                    onRegenerate={(data) => setDocumentData(data)}
                />
            )}
        </div>
    );
};

export default GenerateAgreements;
